import React, { useState, useEffect } from 'react';
import { 
  Check, X, Info, AlertCircle, AlertTriangle, 
  Heart, MessageCircle, MapPin, Crown, Zap, Flame
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

export type NotificationType = 
  | 'success' 
  | 'error' 
  | 'warning' 
  | 'info'
  | 'match'
  | 'message'
  | 'location'
  | 'upgrade'
  | 'super-fuck'
  | 'super-marry';

export interface NotificationData {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  avatar?: string;
  timestamp?: Date;
}

interface NotificationToastProps {
  notification: NotificationData;
  onClose: (id: string) => void;
  onAction?: () => void;
}

const NotificationToast: React.FC<NotificationToastProps> = ({
  notification,
  onClose,
  onAction
}) => {
  const { colors } = useTheme();
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(100);

  const duration = notification.duration || 5000;

  useEffect(() => {
    if (duration > 0) {
      const interval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev - (100 / (duration / 100));
          if (newProgress <= 0) {
            setIsVisible(false);
            setTimeout(() => onClose(notification.id), 300);
            return 0;
          }
          return newProgress;
        });
      }, 100);

      return () => clearInterval(interval);
    }
  }, [duration, notification.id, onClose]);

  const getNotificationStyle = () => {
    switch (notification.type) {
      case 'success':
        return {
          backgroundColor: '#10b981',
          icon: Check,
          iconColor: '#ffffff'
        };
      case 'error':
        return {
          backgroundColor: '#ef4444',
          icon: AlertCircle,
          iconColor: '#ffffff'
        };
      case 'warning':
        return {
          backgroundColor: '#f59e0b',
          icon: AlertTriangle,
          iconColor: '#ffffff'
        };
      case 'match':
        return {
          backgroundColor: '#ec4899',
          icon: Heart,
          iconColor: '#ffffff'
        };
      case 'message':
        return {
          backgroundColor: colors.primary,
          icon: MessageCircle,
          iconColor: '#ffffff'
        };
      case 'location':
        return {
          backgroundColor: '#8b5cf6',
          icon: MapPin,
          iconColor: '#ffffff'
        };
      case 'upgrade':
        return {
          backgroundColor: '#fbbf24',
          icon: Crown,
          iconColor: '#ffffff'
        };
      case 'super-fuck':
        return {
          backgroundColor: '#ff4458',
          icon: Flame,
          iconColor: '#ffffff'
        };
      case 'super-marry':
        return {
          backgroundColor: '#22c55e',
          icon: Heart,
          iconColor: '#ffffff'
        };
      default:
        return {
          backgroundColor: colors.primary,
          icon: Info,
          iconColor: '#ffffff'
        };
    }
  };

  const style = getNotificationStyle();
  const Icon = style.icon;

  const handleAction = () => {
    if (onAction) onAction();
    if (notification.action?.onClick) notification.action.onClick();
    onClose(notification.id);
  };

  const formatTimeAgo = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div
      className={`
        relative overflow-hidden rounded-lg shadow-lg transform transition-all duration-300 ease-in-out
        ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
        max-w-sm w-full pointer-events-auto
      `}
      style={{ backgroundColor: colors.surface }}
    >
      {/* Progress bar */}
      {duration > 0 && (
        <div
          className="absolute top-0 left-0 h-1 transition-all ease-linear"
          style={{ 
            backgroundColor: style.backgroundColor,
            width: `${progress}%`
          }}
        />
      )}

      <div className="p-4">
        <div className="flex items-start">
          {/* Icon */}
          <div 
            className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3"
            style={{ backgroundColor: style.backgroundColor }}
          >
            {notification.avatar ? (
              <img 
                src={notification.avatar} 
                alt="Avatar"
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <Icon size={16} style={{ color: style.iconColor }} />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-medium" style={{ color: colors.textPrimary }}>
                {notification.title}
              </p>
              {notification.timestamp && (
                <span className="text-xs" style={{ color: colors.textSecondary }}>
                  {formatTimeAgo(notification.timestamp)}
                </span>
              )}
            </div>
            
            <p className="text-sm" style={{ color: colors.textSecondary }}>
              {notification.message}
            </p>

            {/* Action button */}
            {notification.action && (
              <button
                onClick={handleAction}
                className="mt-2 text-sm font-medium hover:underline transition"
                style={{ color: style.backgroundColor }}
              >
                {notification.action.label}
              </button>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={() => onClose(notification.id)}
            className="flex-shrink-0 ml-2 p-1 rounded-full hover:bg-opacity-10 transition"
            style={{ color: colors.textSecondary }}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationToast;